import { useCallback, useEffect, useMemo } from 'react';

import { LangData } from '@/constants/language';
import { DEFAULT_GUEST_DEST_LANG } from '@/constants/share';
import { VOICE_TYPE, langList } from '@/features/api';
import { useGuestDisplay } from '@/hooks/useGuestDisplay';
import { useLanguageList } from '@/hooks/useLanguageList';
import {
  BrowserLangData,
  convertPullDownMenuItem,
  findBrowserLang,
} from '@/utils/language';

/**
 * ゲスト画面用翻訳言語情報 hooks
 */
export const useGuestLanguageInfo = () => {
  const { uiLang, destlang, isHostSerial } = useGuestDisplay();
  const { destLangList, setDestLangList, setSrcLangList } = useLanguageList();

  /**
   * STT言語一覧取得
   */
  const fetchSttLangList = useCallback(() => {
    langList({
      voiceType: VOICE_TYPE.STT,
      uiLanguage: uiLang,
      isSerialPlan: isHostSerial,
    }).then((response) => {
      setSrcLangList(response);
    });
  }, [isHostSerial, setSrcLangList, uiLang]);

  /**
   * TTS言語一覧取得
   */
  const fetchTtsLangList = useCallback(() => {
    langList({
      voiceType: VOICE_TYPE.TTS,
      uiLanguage: uiLang,
      isSerialPlan: isHostSerial,
    }).then((response) => {
      setDestLangList(response);
    });
  }, [isHostSerial, setDestLangList, uiLang]);

  /**
   * UI言語が変更された時の処理
   */
  useEffect(() => {
    // UI言語が変更された時、再取得して表示言語を切り替える
    fetchTtsLangList();
    fetchSttLangList();
  }, [fetchTtsLangList, fetchSttLangList]);

  /**
   * ゲスト画面用の翻訳先言語リストに対象の言語が含まれるかどうか
   *
   * @param targetLang 対象の翻訳先言語
   * @returns true:含まれている、false:含まれていない
   */
  const isIncludedDestlang = useCallback(
    (targetLang: string): boolean => {
      if (!destLangList || destLangList.length <= 0) {
        return false;
      }

      return destLangList.some(
        (langData: LangData) => langData[targetLang] !== undefined,
      );
    },
    [destLangList],
  );

  /**
   * 翻訳先言語
   *
   * @returns
   */
  const currentDestlang = useMemo((): string => {
    if (destlang) {
      return destlang;
    }

    if (!destLangList || destLangList.length <= 0) {
      return '';
    }

    // ブラウザ言語を取得
    const browserLangData: BrowserLangData = findBrowserLang();
    if (!browserLangData.browserLang) {
      return DEFAULT_GUEST_DEST_LANG;
    }

    // ゲスト画面用翻訳先言語リストに含まれている場合はその言語を返す
    if (isIncludedDestlang(browserLangData.browserLang)) {
      return browserLangData.browserLang;
    }

    // 'en-US'などの表記になっている場合があるので'-'前の文字列で比較
    if (isIncludedDestlang(browserLangData.preBrowserLang)) {
      return browserLangData.preBrowserLang;
    }

    return DEFAULT_GUEST_DEST_LANG;
  }, [destLangList, destlang, isIncludedDestlang]);

  return {
    // 翻訳先言語
    currentDestlang,
    // 翻訳先言語(プルダウンメニュー用)
    ttsLangPullDownList: convertPullDownMenuItem(destLangList),
  };
};
